import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { F, In, Num, Area, Lines, Sel, Btn } from "./kit";

const inDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

export function quoteDefaults(enquiry, service, pricing) {
  return {
    service_category: enquiry.service_category, title: `${enquiry.video_count} × ${enquiry.service_name}${enquiry.company ? ` for ${enquiry.company}` : ""}`,
    deliverables: service?.deliverables || [], exclusions: service?.exclusions || [],
    price_inr: service?.price_inr != null ? service.price_inr * enquiry.video_count : null,
    gst_mode: pricing?.gst_mode === "inclusive" ? "inclusive" : "exclusive", gst_rate: pricing?.gst_rate ?? 18,
    valid_until: inDays(14), delivery_schedule: service?.turnaround_text || "", first_draft_hours: service?.turnaround_hours ?? null,
    revision_limit: service?.revision_limit ?? 2, deposit_percent: 100, notes: "",
  };
}

const preview = (f) => {
  const p = Number(f.price_inr || 0), r = Number(f.gst_rate || 0);
  if (f.gst_mode === "exclusive") return p + Math.round((p * r) / 100);
  return p;
};

export const QuoteBuilder = ({ open, onOpenChange, initial, enquiryId, quoteId, onSaved }) => {
  const [f, setF] = useState({ deposit_percent: 100, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = quoteId ? await api.put(`/admin/quotes/${quoteId}`, f) : await api.post(`/admin/enquiries/${enquiryId}/quotes`, f);
      toast.success(quoteId ? "New quote version saved" : "Quote drafted");
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#0a0a0a] border-[#27272A] max-h-[90vh] overflow-auto rounded-none" data-testid="quote-builder">
        <DialogHeader><DialogTitle className="font-display text-2xl">{quoteId ? "Revise quote" : "Build quote"}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <F label="Service category"><Sel value={f.service_category} onChange={set("service_category")} options={["Simple", "Complex", "Premium"]} testid="quote-category" /></F>
          <F label="Title"><In value={f.title} onChange={set("title")} testid="quote-title" /></F>
          <F label="Deliverables" className="md:col-span-2"><Lines value={f.deliverables} onChange={set("deliverables")} rows={4} testid="quote-deliverables" /></F>
          <F label="Exclusions" className="md:col-span-2"><Lines value={f.exclusions} onChange={set("exclusions")} rows={3} testid="quote-exclusions" /></F>
          <F label="Proposed price (₹, before GST treatment)"><Num value={f.price_inr} onChange={set("price_inr")} min={0} testid="quote-price" /></F>
          <F label="GST treatment"><Sel value={f.gst_mode} onChange={set("gst_mode")} options={[{ value: "exclusive", label: "Exclusive (added on top)" }, { value: "inclusive", label: "Inclusive" }, { value: "not_applicable", label: "Not applicable" }]} testid="quote-gst-mode" /></F>
          <F label="GST rate %"><Num value={f.gst_rate} onChange={set("gst_rate")} min={0} max={40} testid="quote-gst-rate" /></F>
          <F label="Valid until"><input type="date" className="eb-input !py-2.5" value={f.valid_until} onChange={(e) => set("valid_until")(e.target.value)} data-testid="quote-valid-until" /></F>
          <F label="Delivery schedule" className="md:col-span-2"><Area value={f.delivery_schedule} onChange={set("delivery_schedule")} rows={2} testid="quote-schedule" /></F>
          <F label="First-draft hours after start" hint="Leave empty if the deadline will be set manually"><Num value={f.first_draft_hours} onChange={set("first_draft_hours")} min={1} testid="quote-first-draft-hours" /></F>
          <F label="Revision rounds"><Num value={f.revision_limit} onChange={set("revision_limit")} min={0} testid="quote-revisions" /></F>
          <F label="Advance payment % to start" hint="100 = full payment upfront. Client pays this online after accepting."><Num value={f.deposit_percent} onChange={set("deposit_percent")} min={1} max={100} testid="quote-deposit-percent" /></F>
          <F label="Terms / notes" className="md:col-span-2"><Area value={f.notes} onChange={set("notes")} rows={3} testid="quote-notes" /></F>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-[#27272A]">
          <div className="text-sm text-zinc-400">
            <p>Client pays: <span className="font-display text-2xl text-white" data-testid="quote-preview-total">{formatINR(preview(f))}</span></p>
            <p className="text-xs">Due on acceptance: <span className="text-white" data-testid="quote-preview-due">{formatINR(Math.round((preview(f) * Number(f.deposit_percent || 0)) / 100))}</span></p>
          </div>
          <Btn onClick={save} disabled={busy || f.price_inr == null || !(f.deposit_percent >= 1 && f.deposit_percent <= 100)} data-testid="quote-save">{busy ? "Saving" : "Save draft"}</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
};
